import type { RouteStop } from '@/domains/routeStop/types/RouteStop';
import { db } from '@/db/dexieClient';
import { uow, UnitOfWork } from '@/db/unitOfWork';
import { liveQuery } from 'dexie';
import { upsertEntities, type SyncableEntity, type UpsertConfig } from '@/services/sync/syncUtils';
import { syncService, SyncJobTypes } from '@/services/sync/syncService';
import { addressService } from '@/domains/address/services/addressService';

const routeStopsByRouteIdQuery = (routeId: number) =>
  db.routeStops.where('routeId').equals(routeId).toArray();

const routeStopByIdQuery = (routeStopId: number) => db.routeStops.get(routeStopId);

// Generic partial type for updates
// For Dexie .update, use shallow updates (no nested objects/arrays, just the ones that potentially might change)
type RouteStopUpdate = Partial<
  Pick<
    RouteStop,
    | 'arrivalLatitude'
    | 'arrivalLongitude'
    | 'arriveByTime'
    | 'arrivedOn'
    | 'departedOn'
    | 'departureLatitude'
    | 'departureLongitude'
    | 'syncStatus'
  >
>;

// --- Public API: Get Operations ---
export const getAllRouteStopsFromCache = async (): Promise<RouteStop[]> => {
  try {
    return await db.routeStops.toArray();
  } catch (error) {
    console.error('Failed to get all route stops from cache:', error);
    return [];
  }
};

export const getRouteStopsFromCacheByRouteId = async (routeId: number): Promise<RouteStop[]> => {
  try {
    return await routeStopsByRouteIdQuery(routeId);
  } catch (error) {
    console.error('Failed to get routeStops from cache by routeId:', error);
    return [];
  }
};

export const getRouteStopFromCacheById = async (routeStopId: number): Promise<RouteStop | null> => {
  try {
    return (await routeStopByIdQuery(routeStopId)) ?? null;
  } catch (error) {
    console.error('Failed to get routeStop from cache by id:', error);
    return null;
  }
};

export const observeRouteStopById = (routeStopId: number) =>
  liveQuery(async () => (await routeStopByIdQuery(routeStopId)) ?? null);

export const observeRouteStopsByRouteId = (routeId: number) =>
  liveQuery(async () => (await routeStopsByRouteIdQuery(routeId)) ?? []);

export const observeAllRouteStops = () =>
  liveQuery(async () => (await db.routeStops.toArray()) ?? []);

// --- Public API: Update Operations ---
export const updateRouteStop = async (id: number, changes: RouteStopUpdate): Promise<void> => {
  const oldRecord = (await db.routeStops.get(id)) as RouteStop;
  const newRecord = {
    ...oldRecord,
    ...changes,
  };
  await uow.runRW([db.routeStops], async () => db.routeStops.update(id, changes));

  try {
    await syncService.enqueue({
      type: SyncJobTypes.ROUTE_STOP_UPDATE,
      newRecord: newRecord,
      oldRecord: oldRecord,
    });
  } catch (error) {
    console.warn('[routeStopRepository] Failed to enqueue route stop update sync job:', {
      routeStopId: id,
      error: error instanceof Error ? error.message : String(error),
      changes,
    });
    throw error;
  }
};

// --- Public API: Sync Operations ---
export const upsertRouteStops = async (apiRouteStops: RouteStop[]): Promise<RouteStop[]> => {
  try {
    const cachedRouteStops = await getAllRouteStopsFromCache();
    // Define the configuration for the route stop upsert operation
    const config: UpsertConfig<RouteStop & SyncableEntity> = {
      getId: (routeStop) => routeStop.id,
      markAsPending: (routeStop) => ({
        ...routeStop,
        syncStatus: 'pending',
        syncedOn: null,
      }),
    };

    const { toInsert, toUpdate, toDelete, toKeepLocal } = upsertEntities(
      apiRouteStops as (RouteStop & SyncableEntity)[],
      cachedRouteStops as (RouteStop & SyncableEntity)[],
      config
    );

    await uow.runRW([db.routeStops], async () => {
      // Insert new route stops
      if (toInsert.length > 0) {
        const plainRouteStops = UnitOfWork.toPlain(toInsert);
        await db.routeStops.bulkPut(plainRouteStops);
      }

      // Update existing route stops with API data
      if (toUpdate.length > 0) {
        const plainRouteStops = UnitOfWork.toPlain(toUpdate);
        await db.routeStops.bulkPut(plainRouteStops);
      }

      // Update route stops that should be kept locally (mark as pending)
      if (toKeepLocal.length > 0) {
        const plainRouteStops = UnitOfWork.toPlain(toKeepLocal);
        await db.routeStops.bulkPut(plainRouteStops);
      }

      // Delete route stops not in API response
      if (toDelete.length > 0) {
        await db.routeStops.bulkDelete(toDelete);
      }
    });

    // Get updated route stops from cache after upsert
    const updatedRouteStops = await getAllRouteStopsFromCache();

    console.info('[routeStopRepository] Sync completed:', {
      inserted: toInsert.length,
      updated: toUpdate.length,
      deleted: toDelete.length,
      keptLocal: toKeepLocal.length,
    });

    if (toKeepLocal.length > 0)
      console.warn('[routeStopRepository] Route stops kept local:', toKeepLocal);

    // Load missing addresses from route stops
    await addressService.loadMissingAddressesFromEntities(updatedRouteStops);

    return updatedRouteStops;
  } catch (error) {
    console.error('Failed to upsert route stops:', error);
    throw error;
  }
};
